import { CSSProperties, FC, useCallback, useState } from 'react';
import cn from 'clsx';

import { Dialog } from '@headlessui/react';

import ImageIcon from '@images/imageViewPollInformationIcon.svg';
import VideoIcon from '@images/videoPlayerPollInformationIcon.svg';
import { CrossIcon } from '@components/elements/Icons';

import './Card.css';

import { makeStyles } from '@mui/styles';
const useStyles = (neon) =>
  makeStyles({
    ccard: {
      border: `solid 4px ${neon}`,
    },
    ccard_active: {
      border: 'solid 4px transparent !important',
    },
  });

export type PollOptionCardProps = {
  color?: string;
  disabled?: boolean;
  title: string;
  description?: string;
  price?: string;
  image?: string;
  video?: string;
  active?: boolean;
  style?: CSSProperties;
  neon?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const PollOptionCard: FC<PollOptionCardProps> = ({
  color = 'yellow',
  disabled,
  title,
  description,
  price,
  image,
  video,
  onClick,
  active,
  style,
  neon,
}) => {
  const [modal, setModal] = useState<null | 'video' | 'image'>(null);
  const [modalContent, setModalContent] = useState<JSX.Element>();
  const classes = useStyles(neon)();
  const onHideMedia = useCallback(() => {
    setModal(null);
  }, [setModal]);

  const showMedia = useCallback(
    (type: 'image' | 'video') => {
      if (type === 'image' && image) {
        setModalContent(
          <div
            className="flex items-center justify-center cursor-zoom-out"
            onClick={onHideMedia}
          >
            <div className="relative w-full h-full select-none lg:max-w-screen-lg">
              <div className="absolute top-0 right-0 z-30 m-4">
                <button
                  className="p-2 text-lg text-white bg-black bg-opacity-50 rounded-full hover:bg-opacity-80 focus:outline-none"
                  onClick={onHideMedia}
                >
                  <CrossIcon />
                </button>
              </div>
              <img
                src={image}
                className="object-cover w-full h-auto"
                alt="option"
              />
            </div>
          </div>
        );
        setModal('image');
      } else if (type === 'video' && video) {
        setModalContent(
          <div
            className="flex items-center justify-center cursor-zoom-out"
            onClick={onHideMedia}
          >
            <div className="w-full select-none bg-gray-darkest lg:max-w-screen-lg">
              <div
                style={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                }}
              >
                <div className="absolute top-0 right-0 z-30 m-4">
                  <button
                    className="p-2 text-lg text-white bg-black bg-opacity-50 rounded-full hover:bg-opacity-80 focus:outline-none"
                    onClick={onHideMedia}
                  >
                    <CrossIcon />
                  </button>
                </div>
                <iframe
                  title="poll video"
                  className="absolute top-0 left-0 w-full h-full cursor-default"
                  width="560"
                  height="315"
                  src={video}
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        );
        setModal('video');
      }
    },
    [image, video, onHideMedia]
  );

  let priceDisplay = price;
  const currencyFormatter = new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
  });
  if (price) {
    try {
      priceDisplay = currencyFormatter.format(parseFloat(price));
    } catch (err: any) {
      console.error(err);
    }
  } else {
    priceDisplay = '$' + price;
  }

  return (
    <>
      <Dialog
        open={!!modal}
        onClose={onHideMedia}
        className={
          'fixed inset-0 z-10 overflow-y-auto flex justify-center items-center'
        }
      >
        <div className="relative z-20 w-full m-8">{modalContent}</div>

        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30 cursor-zoom-out" />
      </Dialog>
      <div
        role="button"
        className={cn(
          `flex justify-between py-6 px-4 text-poll-questionCard-pollOption-color ${
            classes.ccard
          } ${
            active
              ? `bg-magenta ` + classes.ccard_active
              : 'bg-poll-questionCard-pollOption-bgColor'
          }`,
          {
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled && onClick !== undefined,
          }
        )}
        onClick={onClick}
        style={{
          minHeight: 100,
          wordBreak: 'break-all',
          ...style,
        }}
      >
        <div className={cn('flex flex-col w-full')}>
          <h3
            className={cn('text-2xl text-poll-questionCard-answer-color', {
              'mb-4': description !== undefined,
            })}
          >
            {title}
          </h3>
          {price && <p>{priceDisplay}</p>}
          {description && (
            <p
              className={cn({
                'text-base': description.length <= 110,
                'text-sm': description.length > 110,
              })}
            >
              {description}
            </p>
          )}
        </div>

        {(image || video) && (
          <div className="flex flex-col w-16">
            {image && (
              <div
                role="button"
                className="p-2 cursor-zoom-in"
                onClick={() => showMedia('image')}
              >
                <img
                  className="border-2 border-white rounded-sm object-fit"
                  src={image || ImageIcon}
                  alt="icon"
                />
              </div>
            )}
            {video && (
              <div
                role="button"
                className="p-2 cursor-pointer"
                onClick={() => showMedia('video')}
              >
                <img
                  className="w-full h-full object-fit"
                  src={VideoIcon}
                  alt="video icon"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PollOptionCard;
